import CheckIcon from "@mui/icons-material/Check";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  Grid2,
  Paper,
  Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { PimoReactComponent } from "@pimo/pimo-app-builder";
import { usePimoForm } from "@pimo/pimo-components/src/lib/pimo-components/use-pimo-form/use-pimo-form.hook";
import { getLocalizedMessage } from "@pimo/pimo-l10n";
import React from "react";
import {
  APP_CRITICALITIES,
  APP_LIFE_CYCLES,
  APPLICATION_CAPABILITIES,
  Configuration,
  ExtendedFieldError,
  FieldDefinition,
  ITEM_NAMES,
  PROCESS_STEPS,
  TECHNICAL_EXPOSURE,
  VISIBLE_APP_RECORD_TYPES,
} from "vmd-types";

import { SUCCESS_COLORS, TOGGLE_GREEN } from "../../constants/colors";
import { AntSwitch } from "../components/ant-switch";
import { CategoryFields } from "../custom-fields/category-field";
import { DefaultFields } from "../custom-fields/default-field";
import { FixedEnumValuesPreview } from "../custom-fields/fixed-enum-values-preview";

// Fields backed by a fixed, non-per-configuration application enum (Postgres enum columns,
// not admin-editable option lists like dropdownFieldOneDefinition). Shown read-only in Settings.
const FIXED_ENUM_FIELDS: Record<string, readonly string[]> = {
  recordType: VISIBLE_APP_RECORD_TYPES,
  criticality: Object.values(APP_CRITICALITIES),
  lifeCycle: Object.values(APP_LIFE_CYCLES),
  processSteps: Object.values(PROCESS_STEPS),
  applicationCapabilities: Object.values(APPLICATION_CAPABILITIES),
  technicalExposure: Object.values(TECHNICAL_EXPOSURE),
  hasContracts: ["yes", "no"],
};

export interface ItemBasicFieldsFormProps {
  configuration?: Configuration;
  language?: string;
  setUpdatedFieldsFlag: (change: "FieldActivated" | "FieldDeactivated") => void;
}

export const ItemBasicFieldsForm: PimoReactComponent<
  ItemBasicFieldsFormProps,
  "define:item-data",
  Configuration
> = ({ language = "en", setUpdatedFieldsFlag }) => {
  const { setValue, formState } = usePimoForm<{
    configuration: Configuration;
    validationErrors: Record<string, ExtendedFieldError>;
    enableSaveButton: boolean;
  }>({
    appStateSlice: "editSettingsForm",
  });

  const itemName =
    ITEM_NAMES.find(
      (entry) =>
        entry.single === (formState.configuration?.itemName ?? "Tracking Item")
    ) ?? ITEM_NAMES[0];
  const [isAccordionExpanded, setIsAccordionExpanded] = React.useState<
    string | false
  >(false);

  const handleChange =
    (panel: string) => (_: React.SyntheticEvent, newExpanded: boolean) => {
      setIsAccordionExpanded(newExpanded ? panel : false);
    };
  return (
    <Box
      sx={{
        p: 2,
        display: "flex",
        flexDirection: "column",
        gap: 2,
        height: "calc(100vh - 83px)",
        overflowY: "auto",
      }}
    >
      <Typography
        sx={{
          fontSize: 24,
          fontWeight: 500,
        }}
      >
        {(() => {
          const localizedText = getLocalizedMessage(
            language,
            "dataModel.masterDataItem"
          );
          return localizedText || "Master Data";
        })()}{" "}
        {itemName.single}
      </Typography>
      <Typography
        sx={{
          fontSize: "14px",
          color: grey[600],
        }}
      >
        {itemName.single}{" "}
        {getLocalizedMessage(language, "dataModel.itemMasterDataDescription") ||
          `master data is the data that is entered when a new ${itemName.single} is created. Please select the data fields`}
      </Typography>

      <Paper
        sx={{
          borderRadius: "12px",
          boxShadow: "none",
          border: "1px solid",
          borderColor: grey[300],
        }}
      >
        <Grid2
          size={12}
          container
          alignItems="center"
          sx={{ height: 65, p: 2 }}
        >
          <Grid2 size={3} sx={{ fontSize: "14px", fontWeight: 500 }}>
            {getLocalizedMessage(language, "dataField") || "Data Field"}
          </Grid2>
          <Grid2 size={3} sx={{ fontSize: "14px", fontWeight: 500 }}>
            {getLocalizedMessage(language, "dataType") || "Data Type"}
          </Grid2>
          <Grid2 size={3}></Grid2>
          <Grid2 size={3}></Grid2>
        </Grid2>

        {formState.configuration?.fieldsDefinition
          ?.filter((f) => f.purpose === "itemBasic")
          .map((field) => (
            <Accordion
              expanded={isAccordionExpanded === field.fieldName}
              onChange={handleChange(field.fieldName)}
              elevation={0}
              key={field.fieldName + field.purpose}
            >
              <AccordionSummary
                aria-controls="panel1-content"
                id="panel1-header"
                expandIcon={
                  field.fieldInConfiguration ||
                  FIXED_ENUM_FIELDS[field.fieldName] ? (
                    <ExpandMoreIcon />
                  ) : null
                }
                sx={{
                  "& .MuiAccordionSummary-content": {
                    marginLeft: 0,
                  },
                  cursor: "default",
                  userSelect: "text",
                  width:
                    field.fieldInConfiguration ||
                    FIXED_ENUM_FIELDS[field.fieldName]
                      ? "100%"
                      : "98%",
                  height: 65,
                }}
              >
                <Grid2
                  size={12}
                  container
                  alignItems="center"
                  key={field.fieldName + field.purpose + "grid"}
                  sx={{ height: 65 }}
                >
                  <Grid2 size={3} sx={{ fontSize: "14px" }}>
                    {field.label}
                  </Grid2>
                  <Grid2 size={3} sx={{ fontSize: "14px" }}>
                    {field.type}
                  </Grid2>
                  <Grid2 size={3}>
                    <FormControlLabel
                      labelPlacement="start"
                      sx={{
                        "& .MuiFormControlLabel-label": {
                          fontSize: "14px",
                        },
                      }}
                      control={
                        <Checkbox
                          onClick={(e) => {
                            // stop the click from bubbling to AccordionSummary
                            e.stopPropagation();
                          }}
                          onChange={(e) => {
                            const currentField = {
                              ...field,
                              isMandatory: e.target.checked,
                            };
                            const newFieldsDefinition =
                              formState.configuration?.fieldsDefinition ?? [];
                            // Find the correct index in the full array
                            const fullArrayIndex =
                              newFieldsDefinition.findIndex(
                                (f) =>
                                  f.fieldName === field.fieldName &&
                                  f.purpose === field.purpose
                              );

                            if (fullArrayIndex !== -1) {
                              newFieldsDefinition[fullArrayIndex] =
                                currentField;
                              setValue(
                                `configuration.fieldsDefinition`,
                                newFieldsDefinition
                              );
                            }
                            if (formState.enableSaveButton !== true) {
                              setValue("enableSaveButton", true);
                            }
                          }}
                          disabled={!field.isEditable}
                          icon={
                            <span
                              style={{
                                width: 20,
                                height: 20,
                                border: "2px solid",
                                borderColor: grey[700],
                                display: "inline-block",
                                backgroundColor: "#fff",
                              }}
                            />
                          }
                          checkedIcon={
                            <span
                              style={{
                                width: 20,
                                height: 20,

                                border: "2px solid",
                                borderColor: !field.isEditable
                                  ? grey[400]
                                  : grey[700],
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                backgroundColor: "#fff",
                              }}
                            >
                              <CheckIcon
                                style={{
                                  fontSize: 18,
                                  color: !field.isEditable
                                    ? grey[400]
                                    : grey[700],
                                }}
                              />
                            </span>
                          }
                          checked={field.isMandatory}
                        />
                      }
                      label={
                        getLocalizedMessage(language, "mandatory") ||
                        "Mandatory"
                      }
                    />
                  </Grid2>
                  <Grid2 size={3} sx={{ fontSize: "14px" }}>
                    {field.fieldName === "customId" ||
                    field.fieldName === "name" ? (
                      <Chip
                        label={
                          getLocalizedMessage(
                            language,
                            "constantlyActiveField"
                          ) || "Constantly Active Field"
                        }
                        sx={{
                          color: SUCCESS_COLORS.text,
                          fontWeight: 500,
                          backgroundColor: SUCCESS_COLORS.background,
                        }}
                      ></Chip>
                    ) : (
                      <FormControlLabel
                        control={
                          <AntSwitch
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                            checked={field.enabled}
                            onChange={(e) => {
                              const currentField = {
                                ...field,
                                enabled: e.target.checked,
                              };

                              const newFieldsDefinition = [
                                ...(formState.configuration?.fieldsDefinition ??
                                  []),
                              ];
                              // Find the correct index in the full array
                              const fullArrayIndex =
                                newFieldsDefinition.findIndex(
                                  (f) =>
                                    f.fieldName === field.fieldName &&
                                    f.purpose === field.purpose
                                );

                              if (fullArrayIndex !== -1) {
                                newFieldsDefinition[fullArrayIndex] =
                                  currentField;
                                setValue(
                                  `configuration.fieldsDefinition`,
                                  newFieldsDefinition
                                );
                              }
                              setValue("enableSaveButton", true);
                              setUpdatedFieldsFlag(
                                e.target.checked
                                  ? "FieldActivated"
                                  : "FieldDeactivated"
                              );
                            }}
                          />
                        }
                        label={
                          getLocalizedMessage(language, "activate") ||
                          "Activate"
                        }
                        labelPlacement="start"
                        sx={{
                          "& .MuiSwitch-thumb": {
                            backgroundColor: grey[300],
                          },
                          "& .MuiSwitch-track": {
                            color: TOGGLE_GREEN,
                          },
                          "& .MuiFormControlLabel-label": {
                            fontSize: "14px",
                          },
                        }}
                      />
                    )}
                  </Grid2>
                </Grid2>
              </AccordionSummary>
              {renderTheCorrectComponent(
                setValue,
                formState.configuration,
                formState.validationErrors,
                field,
                formState.enableSaveButton
              )}
            </Accordion>
          ))}
      </Paper>
    </Box>
  );
};

const renderTheCorrectComponent = (
  setValue: (key: string, value: unknown) => void,
  configuration: Configuration | undefined,
  validationErrors: Record<string, ExtendedFieldError>,
  field: FieldDefinition & {
    purpose:
      "projectReporting" | "itemReporting" | "projectBasic" | "itemBasic";
  },
  enableSaveButton?: boolean
) => {
  switch (field.fieldInConfiguration) {
    case "dropdownFieldOneDefinition":
      return (
        <AccordionDetails>
          <CategoryFields
            baseFieldName="dropdownFieldOneDefinition"
            initialEntries={configuration?.dropdownFieldOneDefinition ?? []}
            onEntriesChange={(val) => {
              setValue(`configuration.dropdownFieldOneDefinition`, val);

              const newErrors: Record<string, ExtendedFieldError> = {
                ...(validationErrors ?? {}),
              };

              Object.keys(newErrors).forEach((k) => {
                if (k.startsWith("dropdownFieldOneDefinition."))
                  delete newErrors[k];
              });

              val.forEach((item, index) => {
                if ((item.name ?? "").trim() === "") {
                  newErrors[`dropdownFieldOneDefinition.${index}.name`] = {
                    type: "required",
                    message: "This field is required",
                    tabContainingErrors: `Master Data ${configuration?.itemName}`,
                  } as ExtendedFieldError;
                }
              });

              setValue("validationErrors", newErrors);
              if (enableSaveButton !== true) {
                setValue("enableSaveButton", true);
              }
            }}
            fieldName={field.label}
            onFieldNameChange={(name) => {
              const indexOfField =
                configuration?.fieldsDefinition?.findIndex(
                  (f) =>
                    f.fieldName === field.fieldName &&
                    f.purpose === field.purpose
                ) ?? 0;
              const newFieldsDefinition = configuration?.fieldsDefinition ?? [];
              newFieldsDefinition[indexOfField] = {
                ...newFieldsDefinition[indexOfField],
                label: name,
              };

              if (name.trim() === "") {
                const newErrors: Record<string, ExtendedFieldError> = {
                  ...(validationErrors ?? {}),
                  [`dropdownFieldOneDefinition`]: {
                    type: "required",
                    message: "This field is required",
                    tabContainingErrors: `Master Data ${configuration?.itemName}`,
                  } as ExtendedFieldError,
                };
                setValue("validationErrors", newErrors);
              } else {
                const newErrors = { ...(validationErrors ?? {}) };
                delete newErrors[`dropdownFieldOneDefinition`];
                setValue("validationErrors", newErrors);
              }

              setValue(`configuration.fieldsDefinition`, newFieldsDefinition);
              if (enableSaveButton !== true) {
                setValue("enableSaveButton", true);
              }
            }}
            validationErrors={validationErrors}
          />
        </AccordionDetails>
      );
    case "secondCategoryDefinition":
      return (
        <AccordionDetails>
          <CategoryFields
            baseFieldName="secondCategoryDefinition"
            initialEntries={configuration?.secondCategoryDefinition ?? []}
            onEntriesChange={(val) => {
              setValue(`configuration.secondCategoryDefinition`, val);

              const newErrors: Record<string, ExtendedFieldError> = {
                ...(validationErrors ?? {}),
              };

              Object.keys(newErrors).forEach((k) => {
                if (k.startsWith("secondCategoryDefinition."))
                  delete newErrors[k];
              });

              val.forEach((item, index) => {
                if ((item.name ?? "").trim() === "") {
                  newErrors[`secondCategoryDefinition.${index}.name`] = {
                    type: "required",
                    message: "Status name cannot be empty",
                    tabContainingErrors: `Master Data ${configuration?.projectName}`,
                  } as ExtendedFieldError;
                }
              });

              setValue("validationErrors", newErrors);
            }}
            fieldName={field.label}
            onFieldNameChange={(name) => {
              const index =
                configuration?.fieldsDefinition?.findIndex(
                  (f) =>
                    f.fieldName === field.fieldName &&
                    f.purpose === field.purpose
                ) ?? 0;
              const newFieldsDefinition = configuration?.fieldsDefinition ?? [];
              newFieldsDefinition[index] = {
                ...newFieldsDefinition[index],
                label: name,
              };
              setValue(`configuration.fieldsDefinition`, newFieldsDefinition);
            }}
            validationErrors={validationErrors}
          />
        </AccordionDetails>
      );
    default: {
      const fixedValues = FIXED_ENUM_FIELDS[field.fieldName];
      return (
        <AccordionDetails>
          {fixedValues ? (
            <FixedEnumValuesPreview values={fixedValues} />
          ) : (
            <DefaultFields />
          )}
        </AccordionDetails>
      );
    }
  }
};
